import { BrandRole, UserRole } from '@fluenceur/types';
import { useAuth } from '@/contexts/auth-context';

/**
 * Whether the current user may MANAGE brand-level resources for the brand they
 * have selected, as opposed to a read-only member.
 *
 * Mirrors the backend `check_brand_admin_role` middleware, which resolves the
 * role held on that brand: an organization owner holds admin implicitly
 * everywhere, a member holds it where the team screen granted it.
 *
 * It deliberately does not read the flattened `user.role`. That value is
 * platform-wide and no screen updates it when a brand role changes, so a member
 * promoted to admin on a brand was shown as its administrator while every
 * management surface stayed hidden.
 *
 * Use this to gate management surfaces (create/edit/delete). Read/use surfaces
 * (e.g. picking a template in the send flow) must NOT be gated on this.
 */
export function useIsBrandManager(): { canManage: boolean; isLoading: boolean } {
  const { user, isLoading } = useAuth();

  // The dashboard and the API deploy separately, so a session answered by an
  // API that predates `currentBrandRole` carries no such field. Falling back to
  // the platform-wide role for that window keeps every manager working; reading
  // the missing field as "not an admin" would instead hide these surfaces from
  // everyone until the API caught up.
  if (user && user.currentBrandRole === undefined) {
    return {
      canManage: user.role === UserRole.BRAND_OWNER || user.role === UserRole.BRAND_ADMIN,
      isLoading,
    };
  }

  return { canManage: user?.currentBrandRole === BrandRole.ADMIN, isLoading };
}
